import React, { useEffect, useState, useRef } from 'react'
import useResponsive, { useResponsiveRange } from '../hooks/useResponsive';
import { LoggerService } from '../api/LoggerService';
import Spinner from './Spinner';
import { triggerHiddenGtmClick } from '../utils/analytics';

export interface ExplainerVideoProps {
  contentType: 'video' | 'audio';
  isOpen?: boolean;
  onClose?: () => void;
  title?: string;
}

const ExplainerVideo = ({contentType, isOpen = true, onClose, title}: ExplainerVideoProps) => {
  const isMobile = useResponsive('md');
  const isTablet = useResponsiveRange('md', 'lg'); // 768px - 991px
  const [videoError, setVideoError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const baseSrc = contentType === 'video' ? import.meta.env.VITE_EXPLAINER_VIDEOINTERVIEW :
                                          import.meta.env.VITE_EXPLAINER_AUDIOINTERVIEW;
  const src = baseSrc ? `${baseSrc}#t=0.1` : baseSrc;
                                          
  const seekTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleVideoError = async (error: any) => {
    console.error('ExplainerVideo: Video failed to load', error);
    
    await LoggerService.warn('ExplainerVideo failed to load', {
        contentType,
        error: error.message || 'Unknown error',
        videoUrl: src
    }, 'ExplainerVideo');
    
    setVideoError(true);
    setIsLoading(false);
};

const handleVideoLoad = async () => {
    console.log('ExplainerVideo: Video loaded successfully');
    try{
      await LoggerService.info('ExplainerVideo loaded successfully', {
        contentType,
        videoUrl: src
      }, 'ExplainerVideo');
    }catch(err){
      console.warn("ExplainerVideo: Error loading video", err);
    }
    
    setVideoError(false);
    setIsLoading(false);
};

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLeavePiP = () => {
        if (!isModalOpen && video) {
          video.pause();
        }
      };
      
      video.addEventListener('leavepictureinpicture', handleLeavePiP);
      return () => video.removeEventListener('leavepictureinpicture', handleLeavePiP);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && src) {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isSafari) {
        // Force Safari to seek to first frame to generate thumbnail
        const handleLoadedMetadata = () => {
          console.log('🍎 Safari: Forcing seek to generate thumbnail');
          video.currentTime = 0.1; // Seek to 0.1 seconds to generate thumbnail
        };
        
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    }
  }, [src]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) closeModal();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isModalOpen]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    // Exit PiP and pause video before closing modal
    if (videoRef.current) {
      if (document.pictureInPictureElement === videoRef.current) {
        document.exitPictureInPicture().catch(console.error);
      }
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      if (onClose) onClose();
    }, 300);
  };

  return (
    <div>
      {isModalOpen && (
        <div 
          className={`modal-backdrop ${isClosing ? 'modal-fadeOut' : ''}`}
          onClick={closeModal}
          style={{ alignItems: 'flex-start', paddingTop: '8vh' }}
        >
          <div
            className={`modal-dialog modal-sm ${isClosing ? 'modal-fadeOut' : ''}`}
            role="document"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: isMobile ? '90vw' : '40vw',
              margin: '0 auto',
            }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ wordBreak: 'break-word', fontWeight: '500', fontSize: '1.25rem' }}>{title || 'Welcome to Video Screener'}</h5>
                <button type="button" className="close opacity-10" onClick={closeModal} aria-label="Close">
                  <span aria-hidden="true">
                    <img 
                        src='/svgs/times.svg'
                        alt='Close'
                        width="16"
                        height="16"
                        className="text-secondary"
                      />
                  </span>
                </button>
              </div>
              <div 
                className="modal-body explainer-modal-body"
                style={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    whiteSpace: 'pre-wrap',
                }}
                >
                <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    {!videoError ? (
                        <video
                        ref={videoRef}
                        src={src} 
                        controls 
                        preload="metadata" // ✅ Safari NEEDS this for thumbnails
                        playsInline
                        disablePictureInPicture
                        poster="" // ✅ Empty poster to let Safari generate its own
                        muted={false}
                        style={{ 
                          width: '100%', 
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: '0.5rem',
                          opacity: isLoading ? 0 : 1,
                          transition: 'opacity 0.3s ease',
                          backgroundColor: '#000' // ✅ Black background while loading thumbnail
                        }}
                        onError={handleVideoError}
                        onLoadedData={handleVideoLoad}
                        onLoadStart={() => setIsLoading(true)}
                        onLoadedMetadata={() => {
                          setIsLoading(false); // ✅ Stop spinner when metadata loads (thumbnail appears)
                        }}
                        onRateChange={(e) => {
                          const video = e.target as HTMLVideoElement;
                          if (video.playbackRate > 2) video.playbackRate = 2;
                          if (video.playbackRate < 0.25) video.playbackRate = 0.25;
                        }}
                        onSeeking={(e) => {
                          if (seekTimeout.current) {
                            clearTimeout(seekTimeout.current);
                          }
                          seekTimeout.current = setTimeout(() => {
                            // Seeking completed
                          }, 100);
                        }}
                        onPlay={() => {
                          triggerHiddenGtmClick('btn-welcomevideo-start-click');
                          if (videoRef.current?.muted) {
                            videoRef.current.muted = false;
                          }
                        }}
                        id='explainer-video'
                        >
                          <p>Your browser does not support the video tag.</p>
                        </video>
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '200px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            padding: '1rem'
                        }}>
                            <img src="/svgs/video.svg" alt="Video Error" width="48" height="48" style={{ marginBottom: '1rem', opacity: 0.5 }} />
                            <p style={{ margin: '0', color: '#6c757d', textAlign: 'center' }}>
                                Unable to load explainer video
                            </p>
                        </div>
                    )}
                    
                    {isLoading && !videoError && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '0.5rem'
                        }}>
                          <Spinner />
                        </div>
                    )}
                </div>

                <p style={{ 
                    marginBottom: '0.5rem',
                    marginTop: '1.0rem',
                    fontSize: '0.875rem',
                    fontWeight: '400',
                    color: '#3F4254'
                    }}>
                    Enhance your job application journey with our intelligent one-way interview platform.
                </p>
                </div>
            </div>
          </div>
        </div>
      )}
      <button id="btn-welcomevideo-start-click" style={{ display: 'none' }} aria-hidden="true" />
    </div>
  )
}

export default ExplainerVideo